/**
 * Workflow Trigger Service
 *
 * Central orchestrator that bridges record mutations to workflow execution.
 * Called from recordStorageService after successful create/update operations.
 *
 * Handles the dual type system — the UI (workflowQueries.ts) uses one shape
 * while the execution engine (workflowRules.ts) uses another. This service
 * normalizes between them so both sources of rules are evaluated.
 */

import { workflowExecutionService } from './workflowExecutionService';
import { workflowContext } from './workflowContext';
import { getActiveRulesByObjectAsync } from '../workflowRuleStorageRouter';
import type { WorkflowRule as ExecutionRule } from '../../types/workflowRules';

// The UI type from workflowQueries.ts — imported dynamically to avoid
// circular dependency. We read directly from localStorage (crm_workflows).
interface UIWorkflowRule {
  id: string;
  name: string;
  description?: string;
  objectName: string;
  objectLabel: string;
  trigger: 'created' | 'updated' | 'created_or_updated' | 'field_changed';
  triggeredFields?: string[];
  hasConditions: boolean;
  conditions: Array<{
    id: string;
    field: string;
    fieldLabel: string;
    operator: string;
    value: string | number | boolean;
  }>;
  conditionLogic: 'AND' | 'OR';
  actions: Array<{
    id: string;
    type: string;
    order: number;
    fieldUpdates?: Array<{ field: string; fieldLabel: string; value: any; useFormula?: boolean }>;
    emailTemplate?: string;
    emailRecipients?: string[];
    emailSubject?: string;
    emailBody?: string;
    relatedObject?: string;
    recordFields?: Array<{ field: string; fieldLabel: string; value: any }>;
    taskSubject?: string;
    taskDescription?: string;
    taskDueDate?: string;
    taskAssignedTo?: string;
    taskPriority?: string;
    webhookUrl?: string;
    webhookMethod?: string;
    webhookHeaders?: string;
    webhookBody?: string;
  }>;
  isActive: boolean;
  executionCount: number;
  createdBy: string;
  createdDate: string;
  lastModified: string;
  lastModifiedBy: string;
}

type TriggerType = 'create' | 'update';

/**
 * Normalize a UI workflow rule (from workflowQueries.ts / crm_workflows)
 * into the execution engine format (workflowRules.ts).
 */
function normalizeUIRule(rule: UIWorkflowRule): ExecutionRule {
  // Map trigger type
  const triggerMap: Record<string, ExecutionRule['triggerType']> = {
    created: 'onCreateOnly',
    updated: 'onUpdateOnly',
    created_or_updated: 'onCreateAndUpdate',
    field_changed: 'onUpdateOnly', // field_changed is a subset of update
  };

  // Map operator names
  const operatorMap: Record<string, string> = {
    isBlank: 'isEmpty',
    isNotBlank: 'isNotEmpty',
  };

  // Map conditions
  const conditions = rule.conditions.map(c => ({
    id: c.id,
    fieldApiName: c.field,
    operator: (operatorMap[c.operator] || c.operator) as any,
    value: c.value,
  }));

  // Map actions
  const actions: ExecutionRule['actions'] = rule.actions.map(a => {
    const actionTypeMap: Record<string, string> = {
      update_fields: 'fieldUpdate',
      send_email: 'emailAlert',
      create_record: 'createRecord',
      create_task: 'task',
      call_webhook: 'webhook',
    };

    const mappedType = (actionTypeMap[a.type] || a.type) as any;
    let config: any;

    switch (mappedType) {
      case 'fieldUpdate':
        config = {
          fieldApiName: a.fieldUpdates?.[0]?.field || '',
          newValue: a.fieldUpdates?.[0]?.value || '',
        };
        break;
      case 'emailAlert':
        config = {
          templateId: a.emailTemplate || '',
          recipients: (a.emailRecipients || []).map(r => ({ type: 'email' as const, email: r })),
        };
        break;
      case 'createRecord':
        config = {
          objectApiName: a.relatedObject || '',
          fieldMappings: (a.recordFields || []).map(f => ({
            fieldApiName: f.field,
            valueType: 'static' as const,
            value: f.value,
          })),
          linkToTriggerRecord: true,
        };
        break;
      case 'task':
        config = {
          subject: a.taskSubject || 'Task',
          description: a.taskDescription || '',
          assignedTo: a.taskAssignedTo || 'current_user',
          dueDate: a.taskDueDate
            ? { type: 'absolute' as const, date: a.taskDueDate }
            : { type: 'relative' as const, days: 7 },
          priority: a.taskPriority || 'Normal',
          status: 'Open',
        };
        break;
      case 'webhook':
        config = {
          url: a.webhookUrl || '',
          method: a.webhookMethod || 'POST',
          headers: a.webhookHeaders ? JSON.parse(a.webhookHeaders) : {},
          body: a.webhookBody || '',
          includeRecordData: true,
        };
        break;
      default:
        config = {};
    }

    return { id: a.id, type: mappedType, order: a.order, config };
  });

  return {
    id: rule.id,
    name: rule.name,
    description: rule.description,
    objectApiName: rule.objectName,
    isActive: rule.isActive,
    triggerType: triggerMap[rule.trigger] || 'onCreateAndUpdate',
    evaluationCriteria: {
      operator: rule.conditionLogic || 'AND',
      conditions: rule.hasConditions ? conditions : [],
    },
    actions,
    createdDate: rule.createdDate,
    createdBy: rule.createdBy,
    lastModified: rule.lastModified,
    lastModifiedBy: rule.lastModifiedBy,
    executionCount: rule.executionCount,
  };
}

/**
 * Load UI-format rules from mockDataManager / crm_workflows localStorage.
 */
function getUIRulesForObject(objectApiName: string): ExecutionRule[] {
  if (!objectApiName) {
    console.warn('[WorkflowTrigger] getUIRulesForObject called with empty objectApiName — returning []');
    return [];
  }
  try {
    const raw = localStorage.getItem('crm_workflows');
    if (!raw) return [];
    const uiRules: UIWorkflowRule[] = JSON.parse(raw);
    return uiRules
      .filter(r => r.isActive && matchesObject(r.objectName, objectApiName))
      .map(normalizeUIRule);
  } catch (error) {
    console.warn('[WorkflowTrigger] Failed to load UI rules:', error);
    return [];
  }
}

/**
 * Loose object name matching — handles suffix variations.
 * e.g., "Client__c" matches "Client", "Client__dev_c", etc.
 */
function matchesObject(ruleObject: string, recordObject: string): boolean {
  const normalize = (s: string) => s.replace(/__\w+$/, '').toLowerCase();
  return normalize(ruleObject) === normalize(recordObject);
}

/**
 * Check if a field_changed trigger matches by comparing old and new records.
 * Exported for use when field_changed trigger support is fully wired.
 */
export function checkFieldChanged(
  rule: UIWorkflowRule,
  record: any,
  previousRecord: any | null
): boolean {
  if (rule.trigger !== 'field_changed') return true;
  if (!previousRecord || !rule.triggeredFields?.length) return true;

  return rule.triggeredFields.some(field => {
    const oldVal = previousRecord[field];
    const newVal = record[field];
    return String(oldVal ?? '') !== String(newVal ?? '');
  });
}

/**
 * Main entry point — called from recordStorageService after successful
 * create or update. Fire-and-forget; errors are caught and logged.
 */
export async function triggerWorkflows(
  objectApiName: string,
  record: any,
  previousRecord: any | null,
  triggerType: TriggerType
): Promise<void> {
  // Validate inputs — fail fast instead of processing with bad data
  if (!objectApiName) {
    console.warn('[WorkflowTrigger] triggerWorkflows called with empty objectApiName — skipping');
    return;
  }
  if (!record) {
    console.warn('[WorkflowTrigger] triggerWorkflows called with null record — skipping');
    return;
  }

  // Don't re-trigger if we're already inside a workflow action
  // (prevents infinite loops from field update actions)
  if (workflowContext.isInWorkflowContext()) {
    console.log(`[WorkflowTrigger] Skipping — already inside workflow context (depth ${workflowContext.currentDepth})`);
    return;
  }

  console.log(`[WorkflowTrigger] Evaluating rules for ${objectApiName} (trigger: ${triggerType})`);

  try {
    // 1. Get rules from the execution-engine storage (workflowRuleStorageRouter)
    let executionRules: ExecutionRule[] = [];
    try {
      executionRules = await getActiveRulesByObjectAsync(objectApiName);
    } catch {
      // Storage router may fail if Gobodo is down — continue with UI rules
    }

    // 2. Get rules from the UI storage (crm_workflows in localStorage)
    const uiRules = getUIRulesForObject(objectApiName);

    // 3. Merge and deduplicate by ID
    const allRulesMap = new Map<string, ExecutionRule>();
    for (const rule of executionRules) {
      allRulesMap.set(rule.id, rule);
    }
    for (const rule of uiRules) {
      if (!allRulesMap.has(rule.id)) {
        allRulesMap.set(rule.id, rule);
      }
    }

    const allRules = Array.from(allRulesMap.values());

    if (allRules.length === 0) return;

    console.log(`\n⚡ Workflow trigger: ${triggerType} on ${objectApiName} — ${allRules.length} rule(s) to evaluate`);

    // 4. Execute once per trigger event.
    // The execution service already loads and evaluates applicable rules.
    const contextId = `trigger:${objectApiName}:${triggerType}`;
    if (!workflowContext.canExecute(contextId)) return;
    workflowContext.enter(contextId);
    try {
      await workflowExecutionService.executeWorkflows(objectApiName, record, triggerType, allRules);
    } finally {
      workflowContext.exit(contextId);
    }
  } catch (error) {
    // Never let workflow errors propagate to the caller
    console.error('❌ Workflow trigger error (non-fatal):', error);
  }
}
